<script lang="ts">

  import FileSearchIcon from "@lucide/svelte/icons/file-search"
  import Trash2Icon from "@lucide/svelte/icons/trash-2"
  import { createForm } from "@tanstack/svelte-form"
  import { createQuery } from "@tanstack/svelte-query"
  import { z } from "zod/v4"

  import Loader from "$lib/components/loader.svelte"
  import { Button } from "$lib/components/ui/button"
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from "$lib/components/ui/dialog"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { ImageWithFallback } from "$lib/components/ui/image-with-fallback"
  import { Input } from "$lib/components/ui/input"

  import type { DocumentationAsset, RealizationForm } from "../../_types"

  const FormSchema = z.object({
    id: z.number().default(0),
    ref_id: z.string().default(""),
    asset_id: z.number().default(0),
    description: z.string().default(""),
    document: z.union([z.file(), z.null()]).default(null),
    document_url: z.string().default(""),
    document_delete: z.number().default(0),
  })

  const FormEmpty = FormSchema.parse({})

  let {
    open,
    setOpen,
    value,
    save,
  }: {
    open: boolean
    setOpen: (open: boolean) => void
    value: DocumentationAsset
    save: (value: RealizationForm["documentation_assets"][number]) => void
  } = $props()

  const form = createForm(() => ({ defaultValues: FormEmpty }))
  const formWatch = form.useSelector((state) => state.values)

  const query = createQuery(() => ({
    enabled: open,
    queryKey: ["documentation-asset-form", open, value.ref_id, value.id],
    queryFn: async () => {
      form.reset({ ...FormEmpty, ...value })

      return ""
    },
  }))

  const preview = $derived(
    formWatch.current.document ? URL.createObjectURL(formWatch.current.document) : formWatch.current.document_url,
  )

  function handlePreview() {
    if (preview) preview.fileOpen("image")
  }

  function handleDelete() {
    form.setFieldValue("document", null)
    form.setFieldValue("document_url", "")
    form.setFieldValue("document_delete", 1)
  }

  function handleSubmit() {
    save({
      ...value,
      ...formWatch.current,
    })
    setOpen(false)
  }

</script>

<Dialog
  open={open}
  onOpenChange={setOpen}>
  <DialogContent
    class="relative max-w-sm"
    onOpenAutoFocus={(event) => event.preventDefault()}>
    <DialogHeader>
      <DialogTitle>Dokumentasi Aset</DialogTitle>
      <DialogDescription></DialogDescription>
    </DialogHeader>
    <Loader show={query.isFetching}/>
    <form
      action="#"
      autocomplete="off"
      class="min-w-0 space-y-6"
      onsubmit={(event) => {
        event.preventDefault()
        handleSubmit()
      }}>
      <div class="space-y-2">
        <ImageWithFallback
          src={preview}
          alt="Dokumentasi Aset"
          class="h-96"/>
        <form.Field name="document">
          {#snippet children(field)}
            <div>
              <FieldLabel>Dokumen</FieldLabel>
              <div class="flex gap-2">
                <Input
                  type="file"
                  class="flex-1"
                  accept=".png,.jpg,.jpeg"
                  onchange={(event) => field.handleChange(event.currentTarget.files?.[0] ?? null)}/>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={!formWatch.current.document && !formWatch.current.document_url}
                  onclick={handlePreview}>
                  <FileSearchIcon aria-hidden="true"/>
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  disabled={!formWatch.current.document && !formWatch.current.document_url}
                  onclick={handleDelete}>
                  <Trash2Icon aria-hidden="true"/>
                </Button>
              </div>
              <FieldMessage
                name="document"
                form={form}/>
            </div>
          {/snippet}
        </form.Field>
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onclick={() => setOpen(false)}>
          Tutup
        </Button>
        <Button type="submit">Simpan</Button>
      </DialogFooter>
    </form>
  </DialogContent>
</Dialog>
